import { RolodexEntryType } from 'types'
import { GeckoPublishingState } from 'types/cdfParams/geckoPublishingState'
import { RolodexEntryJson } from 'types/rolodex/rolodexEntries'
import { parseDate, parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'

/**
 * An entry which represents a complete form response for a visual inspection.
 * This is the main entry that links to the visual inspection and contains
 * metadata about the form completion.
 */
export class GeckoVisualInspectionFormResponse extends GeckoEntryBase<
  {
    form_id: string
    form_type: string
    form_description?: string
    completion_date: Date
    inspector_name?: string
    inspector_id?: string
    publishing_state: GeckoPublishingState
  },
  {
    organization: string
    site: string
    unit: string
    [RolodexEntryType.VISUAL_INSPECTION]: string
  },
  Record<string, never>
> {
  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    super(
      json,
      {
        form_id: parseString(json.data.form_id) ?? '',
        form_type: parseString(json.data.form_type) ?? '',
        form_description: parseString(json.data.form_description),
        completion_date: parseDate(json.data.completion_date) ?? new Date(),
        inspector_name: parseString(json.data.inspector_name),
        inspector_id: parseString(json.data.inspector_id),
        publishing_state: GeckoPublishingState.GetOrCreate(
          parseString(json.data.publishing_state) ?? 'working'
        ),
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit) ?? '',
        [RolodexEntryType.VISUAL_INSPECTION]:
          parseString(json.tags[RolodexEntryType.VISUAL_INSPECTION]) ?? '',
      },
      {}
    )
  }

  /**
   * A simple factory method which creates a visual inspection form response from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoVisualInspectionFormResponse(json)
  }
}
